import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getSettings } from '../services/settings';

export type KeyboardPosition = "Bottom Center" | "Top Center" | "Top Left" | "Top Right";

interface KeyboardContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  activeInput: HTMLInputElement | HTMLTextAreaElement | null;
  setActiveInput: (input: HTMLInputElement | HTMLTextAreaElement | null) => void;
  isEnabled: boolean;
  position: KeyboardPosition;
  refreshConfig: () => Promise<void>;
}

const KeyboardContext = createContext<KeyboardContextType | undefined>(undefined);

export const KeyboardProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeInput, setActiveInput] = useState<HTMLInputElement | HTMLTextAreaElement | null>(null);
  const [isEnabled, setIsEnabled] = useState(false);
  const [position, setPosition] = useState<KeyboardPosition>("Bottom Center");

  const checkScreenSize = useCallback((backendEnabled: boolean) => {
    const isMobileOrTablet = window.innerWidth < 1024;
    setIsEnabled(isMobileOrTablet || backendEnabled);
  }, []);

  const refreshConfig = useCallback(async () => {
    const token = localStorage.getItem("token");
    let backendEnabled = false;

    if (token) {
      try {
        const response = await getSettings();
        if (response.success && response.data.settings) {
          backendEnabled = !!response.data.settings.vkb_is_visible;
          setPosition((response.data.settings.vkb_position as KeyboardPosition) || "Bottom Center");
        }
      } catch (error) {
        console.error("Failed to fetch keyboard settings:", error);
      }
    }

    checkScreenSize(backendEnabled);

    // Store backend state to re-check on resize
    (window as any)._keyboardBackendEnabled = backendEnabled;
  }, [checkScreenSize]);

  useEffect(() => {
    refreshConfig();

    const handleResize = () => {
      const backendEnabled = (window as any)._keyboardBackendEnabled || false;
      checkScreenSize(backendEnabled);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [refreshConfig, checkScreenSize]);

  useEffect(() => {
    const handleFocus = (e: FocusEvent | MouseEvent) => {
      if (!isEnabled) return;

      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        const inputTarget = target as HTMLInputElement | HTMLTextAreaElement;

        // Don't show for certain input types
        if (inputTarget instanceof HTMLInputElement) {
          const type = inputTarget.type;
          const validTypes = ['text', 'password', 'tel', 'email', 'url', 'number', 'search'];
          if (!validTypes.includes(type)) return;
        }

        setActiveInput(inputTarget);
        setIsOpen(true);
      }
    };

    const handleDocumentClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;

      // If clicking outside input and outside keyboard, close it
      const isInput = target.tagName === 'INPUT' || target.tagName === 'TEXTAREA';
      const isKeyboard = target.closest('.keyboard-container');

      if (!isInput && !isKeyboard) {
        setIsOpen(false);
      } else if (isInput) {
        // If clicking an input, ensure it opens (handles re-click on focused input)
        handleFocus(e);
      }
    };

    document.addEventListener('focusin', handleFocus);
    document.addEventListener('mousedown', handleDocumentClick);

    return () => {
      document.removeEventListener('focusin', handleFocus);
      document.removeEventListener('mousedown', handleDocumentClick);
    };
  }, [isEnabled]); // Re-run effect if isEnabled changes

  return (
    <KeyboardContext.Provider value={{
      isOpen,
      setIsOpen,
      activeInput,
      setActiveInput,
      isEnabled,
      position,
      refreshConfig
    }}>
      {children}
    </KeyboardContext.Provider>
  );
};

export const useKeyboard = () => {
  const context = useContext(KeyboardContext);
  if (!context) {
    throw new Error('useKeyboard must be used within a KeyboardProvider');
  }
  return context;
};
